import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Store,
  MapPin,
  Phone,
  User,
  Search,
  Filter,
  Download,
  Save,
  X,
  Building,
  Users,
  AlertCircle
} from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  address: '',
  city: '',
  phone: '',
  manager_id: ''
};

const AdminStores = () => {
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Accès refusé. Privilèges administrateur requis.');
      return;
    }
    fetchStores();
    fetchEmployees();
  }, [isAdmin]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getStores();
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Erreur lors du chargement des magasins');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await authAPI.getEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleChange = (e, fieldName = null) => {
    // Handle react-select onChange (passes selected option object)
    if (fieldName) {
      const value = e ? e.value : '';
      setForm((f) => ({ ...f, [fieldName]: value }));
    } else {
      // Handle regular input onChange (passes event object)
      const { name, value } = e.target;
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await authAPI.updateStore(editingId, form);
        toast.success('Magasin modifié');
      } else {
        await authAPI.createStore(form);
        toast.success('Magasin ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchStores();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (store) => {
    setForm({
      name: store.name,
      address: store.address || '',
      city: store.city || '',
      phone: store.phone || '',
      manager_id: store.manager_id || ''
    });
    setEditingId(store.id);
    setShowForm(true);
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce magasin ?')) return;
    try {
      await authAPI.deleteStore(storeId);
      toast.success('Magasin supprimé');
      fetchStores();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const filteredStores = stores.filter(store =>
    String(store.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(store.city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(store.address || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getManagerName = (managerId) => {
    const manager = employees.find(emp => emp.id === managerId);
    return manager ? manager.name : 'Non assigné';
  };

  const employeeOptions = employees.map(employee => ({
    value: employee.id.toString(),
    label: employee.name
  }));

  const tableColumns = [
    {
      key: 'name',
      label: 'Magasin',
      render: (value, store) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Store className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{store.name}</div>
            <div className="text-sm text-neutral-500">{store.city}</div>
          </div>
        </div>
      )
    },
    {
      key: 'address',
      label: 'Adresse',
      render: (value) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (value) => (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'manager_id',
      label: 'Manager',
      render: (value) => (
        <div className="flex items-center">
          <User className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{getManagerName(value)}</span>
        </div>
      )
    },
    {
      key: 'employee_count',
      label: 'Employés',
      render: (value, store) => {
        const storeEmployees = employees.filter(emp => emp.store_id === store.id && emp.role === 'employee');
        return (
          <div className="flex items-center">
            <Users className="w-4 h-4 text-neutral-400 mr-2" />
            <span className="font-medium">{storeEmployees.length}</span>
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, store) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(store)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(store)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(store.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (!isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernCard className="p-8 max-w-md">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-danger-600" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 text-center mb-2">
            Accès refusé
          </h2>
          <p className="text-neutral-600 text-center">
            Privilèges administrateur requis pour accéder à cette page.
          </p>
        </ModernCard>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Gestion des magasins</h1>
          <p className="text-neutral-600">Gérez tous les magasins de votre réseau</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des magasins..."
            onSearch={setSearchTerm}
            className="w-64"
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Nouveau magasin
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Magasins</p>
              <p className="text-2xl font-bold text-neutral-900">{stores.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Store className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Avec Manager</p>
              <p className="text-2xl font-bold text-success-600">
                {stores.filter(store => store.manager_id).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Employés</p>
              <p className="text-2xl font-bold text-warning-600">
                {employees.filter(emp => emp.role === 'employee').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Magasins Actifs</p>
              <p className="text-2xl font-bold text-primary-600">
                {stores.filter(store => store.is_active !== false).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Stores Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des magasins</h3>
            <p className="text-sm text-neutral-600">{filteredStores.length} magasins trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredStores}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier le magasin' : 'Ajouter un magasin'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom du magasin"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Magasin Centre-Ville"
              required
            />

            <ModernInput
              label="Ville"
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="Ex: Paris"
              required
            />

            <ModernInput
              label="Adresse"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Ex: 123 Rue de la Paix"
            />

            <ModernInput
              label="Téléphone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+33 1 23 45 67 89"
            />

            <ModernSelect
              label="Manager"
              value={form.manager_id ? employeeOptions.find(option => option.value === form.manager_id) : null}
              onChange={(selectedOption) => handleChange(selectedOption, 'manager_id')}
              options={employeeOptions}
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default AdminStores; 